import React, { useState, useRef, useEffect } from 'react';
import { Send, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { LLMProvider, LLMModel, Message, Conversation } from '../../types';
import llmService from '../../services/llmService';
import voiceService from '../../services/voiceService';
import { useChatStore } from '../../stores';

interface SimpleChatInterfaceProps {
  selectedLLM: LLMProvider;
  selectedModel: string;
  availableModels: LLMModel[];
}

const SimpleChatInterface: React.FC<SimpleChatInterfaceProps> = ({
  selectedLLM,
  selectedModel,
  availableModels,
}) => {
  const { 
    conversations, 
    activeConversationId, 
    addConversation, 
    updateConversation,
    addMessage 
  } = useChatStore();
  
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(activeConversationId || undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Загрузка сообщений при выборе диалога из БД
  useEffect(() => {
    const loadMessages = async () => {
      if (activeConversationId) {
        console.log('📥 Загружаем сообщения для диалога:', activeConversationId);
        try {
          const loadedMessages = await llmService.getConversationMessages(activeConversationId);
          console.log('✅ Загружено сообщений:', loadedMessages.length);
          setMessages(loadedMessages);
          setCurrentConversationId(activeConversationId);
        } catch (error) {
          console.error('❌ Ошибка загрузки сообщений:', error);
          setMessages([]);
        }
      } else {
        // Новый чат - очищаем сообщения
        console.log('🆕 Новый чат - очищаем сообщения');
        setMessages([]);
        setCurrentConversationId(undefined);
      }
    };
    
    loadMessages();
  }, [activeConversationId]);

  // Автопрокрутка к последнему сообщению
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Подписка на события озвучивания
  useEffect(() => {
    const handleVoiceEvent = (event: any) => {
      console.log('🎤 Voice event:', event.type, event.data);
      if (event.data?.action === 'speaking') {
        if (event.type === 'start') {
          console.log('✅ Начало озвучивания - устанавливаем isSpeaking = true');
          setIsSpeaking(true);
        } else if (event.type === 'end' || event.type === 'error') {
          console.log('✅ Конец озвучивания - устанавливаем isSpeaking = false');
          setIsSpeaking(false);
        }
      }
    };

    voiceService.addEventListener('start', handleVoiceEvent);
    voiceService.addEventListener('end', handleVoiceEvent);
    voiceService.addEventListener('error', handleVoiceEvent);

    return () => {
      voiceService.removeEventListener('start', handleVoiceEvent);
      voiceService.removeEventListener('end', handleVoiceEvent);
      voiceService.removeEventListener('error', handleVoiceEvent);
    };
  }, []);

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setMessage('');
    setError(null);
    setIsLoading(true);

    try {
      console.log('Отправка сообщения:', { 
        message: message.trim(), 
        provider: selectedLLM, 
        model: selectedModel,
        conversationId: currentConversationId 
      });
      
      const response = await llmService.sendMessage({
        message: message.trim(),
        conversationId: currentConversationId,
        provider: selectedLLM,
        model: selectedModel
      });

      console.log('Ответ получен:', response);
      const assistantMessage = response.message;
      setMessages(prev => [...prev, assistantMessage]);

      // Обновляем список диалогов в store (метаданные, без сообщений)
      const conversationIdToUse = response.conversationId || currentConversationId;
      
      if (conversationIdToUse && !currentConversationId) {
        // Новый диалог - создаём в store (без сообщений, они в БД)
        const newConversation: Conversation = {
          id: conversationIdToUse,
          title: message.trim().slice(0, 50), // Первые 50 символов как заголовок
          llmProvider: selectedLLM,
          llmModel: selectedModel,
          messages: [], // Пустой массив - сообщения хранятся в БД
          messageCount: 2,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        console.log('💾 Создаём новый диалог в store:', newConversation.id);
        addConversation(newConversation);
        setCurrentConversationId(conversationIdToUse);
      } else if (conversationIdToUse) {
        // Существующий диалог - обновляем метаданные в store
        console.log('💾 Обновляем метаданные диалога в store:', conversationIdToUse);
        updateConversation(conversationIdToUse, {
          updatedAt: new Date().toISOString(),
          messageCount: messages.length + 2 // +2 для текущих сообщений
        });
      }

      // Автоматическое озвучивание ответа (если включено в настройках)
      const voiceSettings = voiceService.getSettings();
      if (voiceSettings.autoSpeak) {
        try {
          // isSpeaking обновится автоматически через события
          await voiceService.speak(assistantMessage.content);
        } catch (voiceError) {
          console.warn('Ошибка озвучивания (не критично):', voiceError);
        }
      }
      
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      console.error('Тип ошибки:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('Детали ошибки:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'Произошла неизвестная ошибка';
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Проверка разрешений микрофона
  // Полагаемся только на реальный запрос getUserMedia, так как permissions.query ненадежен
  const checkMicrophonePermission = async (): Promise<{ allowed: boolean; error?: string }> => {
    try {
      // Запрашиваем доступ напрямую - это единственный надежный способ
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Если получили доступ, сразу останавливаем поток
      stream.getTracks().forEach(track => track.stop());
      console.log('✅ Доступ к микрофону получен');
      return { allowed: true };
    } catch (err: any) {
      console.error('❌ Ошибка при запросе доступа к микрофону:', err.name, err.message);
      
      // Определяем тип ошибки и возвращаем понятное сообщение
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        return {
          allowed: false,
          error: 'Доступ к микрофону запрещен. Разрешите использование микрофона в настройках браузера:\n1. Нажмите на значок замка 🔒 в адресной строке\n2. Выберите "Разрешения" → "Микрофон"\n3. Выберите "Разрешить"\n4. Обновите страницу и попробуйте снова.'
        };
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        return {
          allowed: false,
          error: 'Микрофон не найден. Проверьте подключение микрофона в настройках системы.'
        };
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        return {
          allowed: false,
          error: 'Микрофон недоступен. Возможно, используется другим приложением. Закройте другие приложения, использующие микрофон, и попробуйте снова.'
        };
      } else if (err.name === 'OverconstrainedError') {
        return {
          allowed: false,
          error: 'Микрофон не поддерживает требуемые настройки. Попробуйте использовать другой микрофон.'
        };
      }
      
      return {
        allowed: false,
        error: `Не удалось получить доступ к микрофону: ${err.message || 'Неизвестная ошибка'}. Проверьте настройки браузера и системы.`
      };
    }
  };

  const handleVoiceInput = async () => {
    // Проверка поддержки браузером
    const isSupported = voiceService.isSupported();
    if (!isSupported.recognition) {
      setError('Голосовой ввод не поддерживается в вашем браузере. Используйте современный браузер с поддержкой MediaRecorder API.');
      return;
    }

    if (isListening) {
      // Останавливаем прослушивание и распознавание
      try {
        await voiceService.stopListening();
        setIsListening(false);
      } catch (error) {
        console.error('Ошибка при остановке записи:', error);
        setIsListening(false);
      }
      return;
    }

    try {
      setError(null);
      
      // Проверяем разрешения микрофона ПЕРЕД установкой isListening
      // Это важно, чтобы кнопка не меняла состояние, если доступ запрещен
      console.log('🔍 Проверка разрешений микрофона...');
      const permissionCheck = await checkMicrophonePermission();
      
      if (!permissionCheck.allowed) {
        // НЕ устанавливаем isListening в true, если доступ запрещен
        // Кнопка останется в исходном состоянии
        setError(permissionCheck.error || 'Доступ к микрофону запрещен.');
        console.error('🚫 Доступ к микрофону запрещен, запись не начата');
        return;
      }
      
      // Только если доступ получен, начинаем запись
      setIsListening(true);
      console.log('✅ Разрешение на микрофон получено, начало записи через Yandex SpeechKit...');
      
      // Запрашиваем разрешение на использование микрофона и начинаем запись
      // startListening вернет Promise, который разрешится при stopListening
      const recognitionPromise = voiceService.startListening();
      
      // Ждем завершения (пользователь должен нажать кнопку снова для остановки)
      // В реальности stopListening будет вызван при повторном нажатии
      recognitionPromise
        .then((transcript) => {
          console.log('Распознанный текст:', transcript);
          setMessage(transcript);
          setIsListening(false);
          
          // Фокус на поле ввода
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        })
        .catch((error) => {
          console.error('Ошибка распознавания речи:', error);
          setIsListening(false);
          
          const errorMessage = error instanceof Error ? error.message : 'Ошибка распознавания речи';
          
          // Более понятные сообщения об ошибках
          if (errorMessage.includes('not supported')) {
            setError('Голосовой ввод не поддерживается в вашем браузере.');
          } else if (errorMessage.includes('No speech detected') || errorMessage.includes('Речь не распознана')) {
            setError('Речь не распознана. Попробуйте говорить громче, четче или ближе к микрофону.');
          } else if (errorMessage.includes('permission') || errorMessage.includes('Permission') || errorMessage.includes('запрещен') || errorMessage.includes('not-allowed')) {
            setError('Доступ к микрофону запрещен. Разрешите использование микрофона в настройках браузера (значок замка в адресной строке).');
          } else if (errorMessage.includes('Yandex SpeechKit') || errorMessage.includes('API ключи')) {
            setError(errorMessage);
          } else if (errorMessage.includes('audio-capture') || errorMessage.includes('Микрофон не найден')) {
            setError('Микрофон не найден или недоступен. Проверьте подключение микрофона в настройках системы.');
          } else {
            setError(`Ошибка распознавания: ${errorMessage}`);
          }
        });

    } catch (error) {
      console.error('Ошибка при начале записи:', error);
      setIsListening(false);
      
      const errorMessage = error instanceof Error ? error.message : 'Ошибка распознавания речи';
      setError(`Ошибка: ${errorMessage}`);
    }
  };

  const handleVoiceOutput = async () => {
    console.log('🔘 handleVoiceOutput вызван, isSpeaking =', isSpeaking);
    if (isSpeaking) {
      // Останавливаем озвучивание (isSpeaking обновится через события)
      console.log('🛑 Вызываем stopSpeaking()');
      voiceService.stopSpeaking();
      return;
    }

    // Озвучиваем последнее сообщение от ассистента
    const lastAssistantMessage = [...messages].reverse().find(msg => msg.role === 'assistant');
    if (lastAssistantMessage) {
      try {
        console.log('🔊 Начинаем озвучивание...');
        // Не устанавливаем isSpeaking вручную - это сделает событие 'start'
        await voiceService.speak(lastAssistantMessage.content);
        console.log('✅ Озвучивание завершено');
      } catch (error) {
        // Игнорируем ошибку прерывания - это нормальная остановка пользователем
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('interrupted') || errorMessage.includes('canceled')) {
          console.log('🛑 Озвучивание прервано пользователем (это нормально)');
          return;
        }
        
        // Реальная ошибка
        console.error('❌ Ошибка озвучивания:', error);
        setError('Ошибка озвучивания ответа.');
      }
    } else {
      setError('Нет сообщений для озвучивания.');
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      {/* Индикатор прослушивания */}
      {isListening && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          textAlign: 'center',
          color: '#dc2626',
          fontWeight: '500',
          animation: 'pulse 2s infinite'
        }}>
          🎤 Слушаю... Говорите четко!
        </div>
      )}

      {/* Область сообщений */}
      <div style={{
        flex: 1,
        padding: '1rem',
        overflowY: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '1rem',
        marginBottom: '1rem',
        background: '#ffffff',
        minHeight: '300px'
      }}>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
          }
        `}</style>
        {messages.length === 0 && !isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#6b7280',
            fontSize: '1rem'
          }}>
            Начните диалог с AI ассистентом
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  background: msg.role === 'user' ? '#0284c7' : '#f3f4f6',
                  color: msg.role === 'user' ? 'white' : '#111827',
                  marginLeft: msg.role === 'user' ? 'auto' : '0',
                  marginRight: msg.role === 'user' ? '0' : 'auto',
                  maxWidth: '70%',
                  wordWrap: 'break-word'
                }}
              >
                <div style={{ marginBottom: '0.25rem', fontWeight: '500' }}>
                  {msg.role === 'user' ? 'Вы' : 'AI Ассистент'}
                </div>
                <div>{msg.content}</div>
                <div style={{
                  fontSize: '0.75rem',
                  opacity: 0.7,
                  marginTop: '0.25rem'
                }}>
                  {new Date(msg.timestamp).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div style={{
                padding: '0.75rem',
                borderRadius: '0.5rem',
                background: '#f3f4f6',
                maxWidth: '70%',
                color: '#6b7280',
                fontStyle: 'italic'
              }}>
                Ассистент печатает...
              </div>
            )}
            
            {error && (
              <div style={{
                padding: '0.75rem',
                borderRadius: '0.5rem',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                marginBottom: '1rem'
              }}>
                Ошибка: {error}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Статус бар */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.5rem 1rem',
        background: '#f9fafb',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        fontSize: '0.875rem',
        color: '#6b7280'
      }}>
        <span>
          Выбранная модель: {availableModels.find(m => m.id === selectedModel)?.name || selectedModel}
        </span>
        <span>LLM: {selectedLLM.toUpperCase()}</span>
      </div>

      {/* Поле ввода */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        padding: '1.5rem',
        background: '#ffffff',
        borderRadius: '1.5rem',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Введите ваше сообщение..."
          style={{
            flex: 1,
            minHeight: '44px',
            maxHeight: '120px',
            padding: '1rem',
            border: '1px solid #e5e7eb',
            borderRadius: '1rem',
            background: '#ffffff',
            color: '#111827',
            fontSize: '1rem',
            fontFamily: 'inherit',
            resize: 'none',
            outline: 'none'
          }}
        />
        
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
          <button
            onClick={handleVoiceInput}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              background: isListening ? '#ef4444' : voiceService.isSupported().recognition ? 'transparent' : '#f3f4f6',
              color: isListening ? 'white' : voiceService.isSupported().recognition ? '#6b7280' : '#9ca3af',
              border: `1px solid ${isListening ? '#ef4444' : '#e5e7eb'}`,
              borderRadius: '50%',
              cursor: isLoading || !voiceService.isSupported().recognition ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              opacity: isLoading || !voiceService.isSupported().recognition ? 0.5 : 1,
              position: 'relative'
            }}
            title={
              !voiceService.isSupported().recognition 
                ? 'Голосовой ввод не поддерживается в вашем браузере'
                : isListening 
                  ? 'Остановить запись' 
                  : 'Начать запись (говорите четко)'
            }
          >
            {isListening ? <MicOff size={20} /> : <Mic size={20} />}
            {isListening && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '12px',
                height: '12px',
                background: '#ef4444',
                borderRadius: '50%',
                border: '2px solid white',
                animation: 'pulse 1s infinite'
              }} />
            )}
          </button>
          
          <button
            onClick={handleVoiceOutput}
            disabled={!isSpeaking && (isLoading || messages.filter(m => m.role === 'assistant').length === 0)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              background: isSpeaking ? '#22c55e' : 'transparent',
              color: isSpeaking ? 'white' : '#6b7280',
              border: `1px solid ${isSpeaking ? '#22c55e' : '#e5e7eb'}`,
              borderRadius: '50%',
              cursor: isSpeaking ? 'pointer' : (!isSpeaking && (isLoading || messages.filter(m => m.role === 'assistant').length === 0)) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              opacity: (!isSpeaking && (isLoading || messages.filter(m => m.role === 'assistant').length === 0)) ? 0.5 : 1,
              position: 'relative'
            }}
            title={
              messages.filter(m => m.role === 'assistant').length === 0
                ? 'Нет сообщений для озвучивания'
                : isSpeaking
                  ? 'Остановить озвучивание'
                  : 'Озвучить последний ответ'
            }
          >
            {isSpeaking ? <VolumeX size={20} /> : <Volume2 size={20} />}
            {isSpeaking && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '12px',
                height: '12px',
                background: '#22c55e',
                borderRadius: '50%',
                border: '2px solid white',
                animation: 'pulse 1s infinite'
              }} />
            )}
          </button>
          
          <button
            onClick={handleSendMessage}
            disabled={!message.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              background: message.trim() ? '#0284c7' : '#e5e7eb',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: message.trim() ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease-in-out'
            }}
            title="Отправить сообщение"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleChatInterface;
